import {
  FilterOptions,
  OrdersFilters,
  OrdersResponse,
  NumberingHistory,
  NumberingHistoryResponse,
  CurrentOrderNumber,
  UpdateOrderNumberRequest
} from '@/types/orders.types';
import { ApiResponse } from '@/types/services';

import { downloadBlob } from './api';
import { baseApi } from './base-api';
import { getFilenameFromHeader } from './helpers';

export const ordersApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getOrdersFilters: builder.query<FilterOptions, number | undefined>({
      query: (divisionId?) => {
        const params = divisionId ? `?division_id=${divisionId}` : '';
        return { url: `/orders/filters${params}` };
      },
      providesTags: ['OrdersFilters'],
      transformResponse: (response: ApiResponse<FilterOptions>): FilterOptions => {
        return response.data;
      }
    }),
    getSequenceFormOptions: builder.query<FilterOptions, number | undefined>({
      query: (divisionId?) => {
        const params = divisionId ? `?division_id=${divisionId}` : '';
        return { url: `/orders/sequence-form-options${params}` };
      },
      providesTags: ['OrdersFilters'],
      transformResponse: (response: ApiResponse<FilterOptions>): FilterOptions => {
        return response.data;
      }
    }),
    getOrders: builder.query<OrdersResponse, OrdersFilters>({
      query: filters => {
        const params = new URLSearchParams();
        if (filters.division_id) params.append('division_id', filters.division_id.toString());
        if (filters.legal_entity_id) {
          if (Array.isArray(filters.legal_entity_id)) {
            filters.legal_entity_id.forEach(id => params.append('legal_entity_id', id.toString()));
          } else {
            params.append('legal_entity_id', filters.legal_entity_id.toString());
          }
        }
        if (filters.date_from) params.append('date_from', filters.date_from);
        if (filters.date_to) params.append('date_to', filters.date_to);
        if (filters.page) params.append('page', filters.page.toString());
        if (filters.limit) params.append('limit', filters.limit.toString());
        return { url: `/orders?${params.toString()}` };
      },
      providesTags: ['Orders'],
      transformResponse: (response: OrdersResponse): OrdersResponse => {
        return {
          orders: response.orders || [],
          total: response.total || 0,
          page: response.page || 1,
          totalPages: response.totalPages || 1,
          limit: response.limit || 20
        };
      }
    }),

    getNumberHistory: builder.query<NumberingHistory[], OrdersFilters>({
      query: filters => {
        const params = new URLSearchParams();
        if (filters.division_id) params.append('division_id', filters.division_id.toString());
        if (filters.legal_entity_id) {
          if (Array.isArray(filters.legal_entity_id)) {
            filters.legal_entity_id.forEach(id => params.append('legal_entity_id', id.toString()));
          } else {
            params.append('legal_entity_id', filters.legal_entity_id.toString());
          }
        }
        if (filters.date_from) params.append('date_from', filters.date_from);
        if (filters.date_to) params.append('date_to', filters.date_to);
        if (filters.page) params.append('page', filters.page.toString());
        if (filters.limit) params.append('limit', filters.limit.toString());
        const queryString = params.toString();
        return { url: `/orders/number-history${queryString ? `?${queryString}` : ''}` };
      },
      providesTags: ['OrdersHistory'],
      transformResponse: (response: ApiResponse<NumberingHistoryResponse>): NumberingHistory[] =>
        response.data.history || []
    }),
    getCurrentOrderNumber: builder.query<CurrentOrderNumber, { divisionId: number; legalEntityId: number }>({
      query: ({ divisionId, legalEntityId }) => ({
        url: `/orders/current-number?division_id=${divisionId}&legal_entity_id=${legalEntityId}`
      }),
      transformResponse: (response: ApiResponse<CurrentOrderNumber>): CurrentOrderNumber => {
        return response.data;
      }
    }),
    updateOrderNumber: builder.mutation<{ success: boolean }, UpdateOrderNumberRequest>({
      query: data => ({
        url: '/orders/update-number',
        method: 'PUT',
        body: data
      }),
      invalidatesTags: ['OrdersHistory']
    }),
    downloadOrder: builder.mutation<string, number>({
      async queryFn(orderId, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `/orders/${orderId}/download`,
          responseHandler: async response => {
            const blob = await response.blob();
            // If the response is JSON, it's an error — extract the message
            if (response.headers.get('content-type')?.includes('application/json')) {
              const text = await blob.text();
              const errorData = JSON.parse(text);
              throw new Error(errorData.error || 'Помилка завантаження файлу');
            }
            return blob;
          }
        });

        if (result.error) {
          return { error: result.error };
        }

        const blob = result.data as Blob;
        const response = result.meta?.response;
        const filename = getFilenameFromHeader(response?.headers.get('content-disposition') ?? null);

        downloadBlob(blob, filename);
        return { data: filename };
      }
    })
  })
});
export const {
  useGetOrdersFiltersQuery,
  useGetSequenceFormOptionsQuery,
  useGetOrdersQuery,
  useGetNumberHistoryQuery,
  useGetCurrentOrderNumberQuery,
  useUpdateOrderNumberMutation,
  useDownloadOrderMutation
} = ordersApi;
